import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MapPin, Minus, Plus, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BASE_PRICES, PRICING, LAGOS_LGAS, TIME_SLOTS, formatNaira } from "../data/site";

const STEPS = ["Service & Price", "Details & Add-ons", "Lagos Location", "Schedule & Contact", "Review"];

const PRICE_SECTIONS = [
  { key: "Residential Cleaning", title: "Residential Cleaning" },
  { key: "Monthly Cleaning Plan", title: "Monthly Cleaning Plans" },
  { key: "Office & Business Cleaning", title: "Office & Business Cleaning" },
  { key: "Move-In / Move-Out Cleaning", title: "Move-In & Move-Out Cleaning" },
  { key: "Event & Hall Cleaning", title: "Event & Hall Cleaning" },
  { key: "After-Party Cleanup", title: "After-Party Cleanup" },
];

function PriceMatrix({ category, active, onSelect }) {
  const cfg = BASE_PRICES[category.key];
  const monthly = category.key === "Monthly Cleaning Plan";
  return (
    <section className={`rounded-2xl border-2 overflow-hidden bg-white transition-all ${active ? "border-brand-lime shadow-md" : "border-[#D6E4DC]"}`}>
      <button type="button" onClick={() => onSelect(category.key)} className={`w-full text-left px-5 py-4 flex items-center justify-between gap-4 ${active ? "bg-brand-mint" : "bg-white"}`}>
        <div>
          <h3 className="font-display font-extrabold text-brand-emerald">{category.title}</h3>
          <p className="text-xs text-brand-muted mt-1">Select this service to book from the complete price table below.</p>
        </div>
        {active ? <span className="inline-flex items-center gap-1 rounded-full bg-brand-lime px-3 py-1 text-xs font-extrabold text-brand-emerald-dark"><Check className="w-3.5 h-3.5" /> Selected</span> : <span className="text-xs font-bold text-brand-emerald">Choose</span>}
      </button>
      <div className="overflow-x-auto border-t border-[#EFF5F1]">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-brand-emerald text-white">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Property / Space</th>
              {cfg.tiers.map((tier) => <th key={tier} className="text-left px-4 py-3 font-bold">{tier}</th>)}
            </tr>
          </thead>
          <tbody>
            {cfg.properties.map((property, rowIndex) => (
              <tr key={property} className={rowIndex % 2 ? "bg-brand-mint/40" : "bg-white"}>
                <td className="px-4 py-3 font-semibold text-brand-ink">{property}</td>
                {cfg.matrix[property].map((price, idx) => (
                  <td key={`${property}-${idx}`} className="px-4 py-3 text-brand-emerald font-bold">From {formatNaira(price)}{monthly ? "/month" : ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const categories = Object.keys(BASE_PRICES);
  const initialCategory = categories.includes(params.get("service")) ? params.get("service") : categories[0];
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const mountedAt = useRef(Date.now());
  const [form, setForm] = useState({
    service_category: initialCategory,
    service_tier: BASE_PRICES[initialCategory].tiers[0],
    property_type: BASE_PRICES[initialCategory].properties[0],
    frequency: initialCategory === "Monthly Cleaning Plan" ? "Monthly" : "One-off",
    addons: [],
    addon_quantities: {},
    lga: "",
    area: "",
    address: "",
    landmark: "",
    date: "",
    time_slot: TIME_SLOTS[0],
    name: "",
    email: "",
    phone: "",
    notes: "",
    website: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cfg = BASE_PRICES[form.service_category];
  const areas = useMemo(() => LAGOS_LGAS.find((l) => l.lga === form.lga)?.areas || [], [form.lga]);
  const tierIdx = cfg.tiers.indexOf(form.service_tier);
  const basePrice = cfg.matrix[form.property_type]?.[tierIdx] ?? 0;

  const addonDetails = useMemo(() => form.addons.map((name) => {
    const addon = PRICING.addons.find((a) => a.name === name);
    const quantity = addon?.quantityBased ? Math.max(1, Number(form.addon_quantities[name] || 1)) : 1;
    const unitPrice = addon?.value || 0;
    return {
      name,
      quantity,
      unit: addon?.unit || "service",
      unit_price: unitPrice,
      subtotal: unitPrice * quantity,
      display_price: addon?.price || formatNaira(unitPrice),
    };
  }), [form.addons, form.addon_quantities]);

  const addonsTotal = useMemo(() => addonDetails.reduce((sum, item) => sum + item.subtotal, 0), [addonDetails]);
  const estimate = basePrice + addonsTotal;

  const pickCategory = (cat) => {
    setForm((f) => ({
      ...f,
      service_category: cat,
      service_tier: BASE_PRICES[cat].tiers[0],
      property_type: BASE_PRICES[cat].properties[0],
      frequency: cat === "Monthly Cleaning Plan" ? "Monthly" : "One-off",
    }));
  };

  const toggleAddon = (addon, checked) => {
    setForm((f) => {
      const addons = checked ? [...f.addons, addon.name] : f.addons.filter((x) => x !== addon.name);
      const addon_quantities = { ...f.addon_quantities };
      if (checked && addon.quantityBased && !addon_quantities[addon.name]) addon_quantities[addon.name] = 1;
      if (!checked) delete addon_quantities[addon.name];
      return { ...f, addons, addon_quantities };
    });
  };

  const setAddonQuantity = (name, quantity) => {
    const safe = Math.min(100, Math.max(1, Number(quantity) || 1));
    setForm((f) => ({ ...f, addon_quantities: { ...f.addon_quantities, [name]: safe } }));
  };

  const canNext = () => {
    if (step === 0) return !!form.service_category;
    if (step === 1) return !!form.service_tier && !!form.property_type;
    if (step === 2) return !!form.lga && form.address.trim().length >= 5;
    if (step === 3) return !!form.date && form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 7;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        addon_details: addonDetails,
        base_price: basePrice,
        addons_total: addonsTotal,
        estimated_total: estimate,
        estimated_price: `From ${formatNaira(estimate)}${form.service_category === "Monthly Cleaning Plan" ? "/month" : ""}`,
        elapsed_ms: Date.now() - mountedAt.current,
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Booking successful.");
      navigate("/thank-you", { replace: true, state: { reference: data.reference, email: form.email, phone: form.phone } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#D6E4DC] bg-white focus:outline-none focus:ring-2 focus:ring-brand-lime/50 focus:border-brand-lime text-sm";
  const labelCls = "block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-1.5";

  return (
    <div className="pt-24 lg:pt-32 pb-20 bg-brand-canvas min-h-screen" data-testid="booking-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-2">Book a Cleaning</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-emerald tracking-tight">Choose from every service and every published price.</h1>
          <p className="mt-3 text-brand-muted max-w-3xl">All prices from the Neat &amp; Proper price lists are built into this booking form. Select your exact service, property size, service level and any add-ons to receive an instant starting estimate.</p>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 mb-10 overflow-x-auto pb-2" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <button data-testid={`booking-step-${i}`} onClick={() => i < step && setStep(i)} className={`flex items-center gap-2 whitespace-nowrap ${i <= step ? "cursor-pointer" : "cursor-default"}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold ${i < step ? "bg-brand-lime text-white" : i === step ? "bg-brand-emerald text-white" : "bg-[#E8EFEB] text-brand-muted"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
                <span className={`text-xs sm:text-sm font-bold ${i === step ? "text-brand-emerald" : "text-brand-muted"}`}>{s}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`h-px w-5 sm:w-10 shrink-0 ${i < step ? "bg-brand-lime" : "bg-[#D6E4DC]"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-[#D6E4DC] shadow-sm p-5 sm:p-8">
          <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          {step === 0 && (
            <div data-testid="booking-step-service">
              <div className="mb-7">
                <h2 className="font-display text-2xl font-bold text-brand-emerald">Complete service price guide</h2>
                <p className="mt-2 text-sm text-brand-muted">Every published base price is shown here. Click any section to make it your booking service.</p>
              </div>
              <div className="space-y-5">
                {PRICE_SECTIONS.map((category) => <PriceMatrix key={category.key} category={category} active={form.service_category === category.key} onSelect={pickCategory} />)}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="booking-step-details">
              <h2 className="font-display text-2xl font-bold text-brand-emerald mb-2">Select the exact price and add-ons</h2>
              <p className="text-sm text-brand-muted mb-6">The base price updates from the same price table shown on the website. All 20 add-on prices are displayed below without a hidden scroll area.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Property / Space Type</label>
                  <select data-testid="booking-property-select" value={form.property_type} onChange={(e) => set("property_type", e.target.value)} className={inputCls}>{cfg.properties.map((p) => <option key={p} value={p}>{p}</option>)}</select>
                </div>
                <div>
                  <label className={labelCls}>Service Level</label>
                  <select data-testid="booking-tier-select" value={form.service_tier} onChange={(e) => set("service_tier", e.target.value)} className={inputCls}>{cfg.tiers.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                </div>
              </div>

              <div className="mt-5 rounded-2xl bg-brand-mint border border-brand-lime/30 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div><p className="text-xs uppercase tracking-wider font-bold text-brand-muted">Selected base service</p><p className="font-bold text-brand-emerald">{form.service_category} · {form.property_type} · {form.service_tier}</p></div>
                <p className="font-display text-xl font-extrabold text-brand-emerald">From {formatNaira(basePrice)}{form.service_category === "Monthly Cleaning Plan" ? "/month" : ""}</p>
              </div>

              <div className="mt-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
                  <div><label className={labelCls}>All Add-On Services (optional)</label><p className="text-sm text-brand-muted">All 20 published add-ons are available here.</p></div>
                  <p className="text-sm font-bold text-brand-emerald">Selected add-ons: {formatNaira(addonsTotal)}</p>
                </div>
                <div className="grid lg:grid-cols-2 gap-3">
                  {PRICING.addons.map((a) => {
                    const checked = form.addons.includes(a.name);
                    const quantity = form.addon_quantities[a.name] || 1;
                    return (
                      <div key={a.name} data-testid={`booking-addon-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-xl border p-4 transition-colors ${checked ? "border-brand-lime bg-brand-mint" : "border-[#D6E4DC] bg-white"}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={(e) => toggleAddon(a, e.target.checked)} className="accent-[#7AB800] w-4 h-4 mt-1" />
                          <span className="flex-1 min-w-0"><span className="block text-sm font-semibold text-brand-ink">{a.name}</span><span className="block text-xs font-extrabold text-brand-emerald mt-1">{a.price}</span></span>
                        </label>
                        {checked && a.quantityBased && (
                          <div className="mt-3 ml-7 flex items-center justify-between gap-3 rounded-lg bg-white border border-[#D6E4DC] px-3 py-2">
                            <span className="text-xs font-bold text-brand-muted uppercase">Number of windows</span>
                            <div className="flex items-center gap-2">
                              <button type="button" aria-label={`Decrease ${a.name} quantity`} onClick={() => setAddonQuantity(a.name, quantity - 1)} className="w-8 h-8 rounded-lg border border-[#D6E4DC] flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                              <input aria-label={`${a.name} quantity`} type="number" min="1" max="100" value={quantity} onChange={(e) => setAddonQuantity(a.name, e.target.value)} className="w-16 h-8 rounded-lg border border-[#D6E4DC] text-center text-sm font-bold" />
                              <button type="button" aria-label={`Increase ${a.name} quantity`} onClick={() => setAddonQuantity(a.name, quantity + 1)} className="w-8 h-8 rounded-lg border border-[#D6E4DC] flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                            </div>
                            <span className="text-xs font-extrabold text-brand-emerald">{formatNaira(a.value * quantity)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="booking-step-location">
              <h2 className="font-display text-xl font-bold text-brand-emerald mb-2 flex items-center gap-2"><MapPin className="w-5 h-5 text-brand-lime" /> Where in Lagos?</h2>
              <p className="text-sm text-brand-muted mb-6">We cover all 20 LGAs of Lagos State — Island and Mainland.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><label className={labelCls}>Local Government Area (LGA)</label><select data-testid="booking-lga-select" value={form.lga} onChange={(e) => setForm((f) => ({ ...f, lga: e.target.value, area: "" }))} className={inputCls}><option value="">Select LGA…</option>{LAGOS_LGAS.map((l) => <option key={l.lga} value={l.lga}>{l.lga}</option>)}</select></div>
                <div><label className={labelCls}>Area / Neighbourhood</label><select data-testid="booking-area-select" value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} disabled={!form.lga}><option value="">{form.lga ? "Select area…" : "Pick an LGA first"}</option>{areas.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
                <div className="sm:col-span-2"><label className={labelCls}>Street Address *</label><input data-testid="booking-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="e.g. 12 Admiralty Way" className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Nearest Landmark (optional)</label><input data-testid="booking-landmark-input" value={form.landmark} onChange={(e) => set("landmark", e.target.value)} placeholder="e.g. Opposite Shoprite" className={inputCls} /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-step-schedule">
              <h2 className="font-display text-xl font-bold text-brand-emerald mb-6">Schedule &amp; your details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><label className={labelCls}>Preferred Date *</label><input data-testid="booking-date-input" type="date" min={new Date().toISOString().split("T")[0]} value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Time Slot</label><select data-testid="booking-time-select" value={form.time_slot} onChange={(e) => set("time_slot", e.target.value)} className={inputCls}>{TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                <div><label className={labelCls}>Full Name *</label><input data-testid="booking-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={inputCls} /></div>
                <div><label className={labelCls}>Phone Number *</label><input data-testid="booking-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="080x xxx xxxx" className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Email Address *</label><input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Special Notes (optional)</label><textarea data-testid="booking-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Pets, access instructions, heavy stains..." className={inputCls} /></div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="booking-step-review">
              <h2 className="font-display text-2xl font-bold text-brand-emerald mb-6">Review your complete booking</h2>
              <div className="grid lg:grid-cols-2 gap-8">
                <dl className="space-y-4 text-sm">
                  {[["Service", `${form.service_category} — ${form.service_tier}`],["Property", form.property_type],["Frequency", form.frequency],["Location", `${form.area ? form.area + ", " : ""}${form.lga}, Lagos`],["Address", form.address + (form.landmark ? ` (${form.landmark})` : "")],["Date & Time", `${form.date} — ${form.time_slot}`],["Contact", `${form.name} · ${form.phone} · ${form.email}`]].map(([k,v]) => <div key={k} className="border-b border-[#EFF5F1] pb-3"><dt className="text-xs font-bold text-brand-muted uppercase tracking-wider">{k}</dt><dd className="mt-1 text-brand-ink font-medium">{v}</dd></div>)}
                </dl>
                <div className="rounded-2xl border border-[#D6E4DC] overflow-hidden">
                  <div className="bg-brand-emerald text-white px-5 py-4"><h3 className="font-display font-bold">Price breakdown</h3></div>
                  <div className="p-5 space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><span className="text-brand-muted">Base service</span><strong className="text-brand-emerald">{formatNaira(basePrice)}</strong></div>
                    {addonDetails.map((item) => <div key={item.name} className="flex justify-between gap-4 border-t border-[#EFF5F1] pt-3"><span className="text-brand-ink">{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ""}</span><strong className="text-brand-emerald">{formatNaira(item.subtotal)}</strong></div>)}
                    {!addonDetails.length && <p className="text-brand-muted border-t border-[#EFF5F1] pt-3">No add-ons selected.</p>}
                    <div className="flex justify-between gap-4 border-t-2 border-brand-lime pt-4 text-base"><span className="font-bold text-brand-ink">Starting estimate</span><strong className="font-display text-xl text-brand-emerald">{formatNaira(estimate)}{form.service_category === "Monthly Cleaning Plan" ? "/month" : ""}</strong></div>
                  </div>
                </div>
              </div>
              <p className="mt-5 text-xs text-brand-muted">All listed prices are starting prices. Final pricing may vary depending on size, quantity, condition, level of dirt, scope of work, equipment required and time. No payment is taken online.</p>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-[#EFF5F1] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2" data-testid="booking-estimate"><Sparkles className="w-4 h-4 text-brand-gold" /><span className="text-sm text-brand-muted">Instant estimate:</span><span className="font-display font-extrabold text-brand-emerald text-xl" data-testid="booking-estimate-value">From {formatNaira(estimate)}{form.service_category === "Monthly Cleaning Plan" ? "/month" : ""}</span></div>
            <div className="flex gap-3">
              {step > 0 && <button data-testid="booking-back-btn" onClick={() => setStep(step - 1)} className="px-6 py-3 rounded-full border-2 border-brand-emerald/20 text-brand-emerald font-bold text-sm hover:border-brand-emerald transition-colors flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>}
              {step < 4 ? <button data-testid="booking-next-btn" onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="px-7 py-3 rounded-full bg-brand-emerald text-white font-bold text-sm hover:bg-brand-lime disabled:opacity-40 transition-colors flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></button> : <button data-testid="booking-submit-btn" onClick={submit} disabled={submitting} className="px-8 py-3 rounded-full bg-brand-lime text-brand-emerald-dark font-extrabold text-sm hover:bg-brand-emerald hover:text-white disabled:opacity-50 transition-colors">{submitting ? "Submitting…" : "Confirm Booking Request"}</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
